import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRightIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

/**
 * Build the visible rows. Up to 10 forecasters are shown verbatim; beyond
 * that we always pin the top 5 (payment zone), ±2 around self, and the
 * last row, collapsing skipped runs into a `{ __sep: true, count }` entry.
 */
function windowAroundSelf(rows, selfUserId) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  if (rows.length <= 10) return rows;

  const selfIdx = rows.findIndex((r) => r.user_id === selfUserId);
  const keepIdx = new Set();
  // Always pin top 5 (payment zone)
  for (let i = 0; i < Math.min(5, rows.length); i++) keepIdx.add(i);
  // Always pin last
  keepIdx.add(rows.length - 1);
  // Pin self ±2
  if (selfIdx >= 0) {
    for (
      let i = Math.max(0, selfIdx - 2);
      i <= Math.min(rows.length - 1, selfIdx + 2);
      i++
    ) {
      keepIdx.add(i);
    }
  }

  const sortedKept = [...keepIdx].sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sortedKept.length; i++) {
    const idx = sortedKept[i];
    out.push(rows[idx]);
    const next = sortedKept[i + 1];
    if (next != null && next > idx + 1) {
      out.push({ __sep: true, count: next - idx - 1 });
    }
  }
  return out;
}

// Map a score to a league tier using the thresholds (lower error = better).
// Returns one of 'elite' | 'challenger' | 'runner_up' | 'contender' | null.
function tierForScore(score, thresholds) {
  if (score == null || !thresholds) return null;
  const { elite, challenger, runner_up: runnerUp } = thresholds;
  if (elite != null && score <= elite) return 'elite';
  if (challenger != null && score <= challenger) return 'challenger';
  if (runnerUp != null && score <= runnerUp) return 'runner_up';
  return 'contender';
}

// Map a qualified-rank to a league tier — mirrors `find_forecaster_league` in
// the engine: ranks 1–5 are Elite, 6–10 Challenger, 11 Runner-Up, 12+ Contender.
// This is the source of truth for row coloring in the rail; using rank avoids
// score-vs-threshold drift when unqualified peers would otherwise pull the
// threshold below the 5th qualified score.
function tierForRank(rank) {
  if (rank == null) return null;
  if (rank <= 5) return 'elite';
  if (rank <= 10) return 'challenger';
  if (rank === 11) return 'runner_up';
  return 'contender';
}

/**
 * Pairwise head-to-head for the viewer against a single peer row.
 * Both args are `[{target_day, score}]` arrays. Lower score wins (RMSE /
 * Winkler / etc. are all error metrics — minimize).
 *
 * Returns { wins, losses, ties, coDays } — viewer's wins, losses, ties on
 * days both submitted. Ties are kept separate so callers can choose their
 * own display rule (we currently fold them into both sides at 0.5).
 */
function h2hAgainstViewer(rowDailyScores, viewerDailyScores) {
  const empty = { wins: 0, losses: 0, ties: 0, coDays: 0 };
  if (!Array.isArray(rowDailyScores) || !Array.isArray(viewerDailyScores)) {
    return empty;
  }
  const v = new Map();
  for (const d of viewerDailyScores) {
    if (d && d.target_day != null && d.score != null) {
      v.set(d.target_day, Number(d.score));
    }
  }
  let wins = 0;
  let losses = 0;
  let ties = 0;
  for (const d of rowDailyScores) {
    if (!d || d.target_day == null || d.score == null) continue;
    if (!v.has(d.target_day)) continue;
    const peerScore = Number(d.score);
    const viewerScore = v.get(d.target_day);
    if (peerScore > viewerScore) wins += 1; // lower viewer score = viewer wins
    else if (peerScore < viewerScore) losses += 1;
    else ties += 1;
  }
  return { wins, losses, ties, coDays: wins + losses + ties };
}

const LOW_OVERLAP_THRESHOLD = 5;

export const __testables__ = {
  windowAroundSelf,
  tierForScore,
  tierForRank,
  h2hAgainstViewer,
};

const tierStyles = {
  elite: {
    border: 'border-l-brand-600 dark:border-l-brand-400',
    rank: 'text-brand-700 dark:text-brand-300',
    bg: '',
    chip: 'bg-brand-50 text-brand-800 dark:bg-brand-500/20 dark:text-brand-200',
    label: 'Elite',
  },
  challenger: {
    border: 'border-l-slate-500 dark:border-l-slate-400',
    rank: 'text-slate-700 dark:text-slate-300',
    bg: '',
    chip: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
    label: 'Challenger',
  },
  runner_up: {
    border: 'border-l-slate-400 dark:border-l-slate-500',
    rank: 'text-slate-600 dark:text-slate-400',
    bg: '',
    chip: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    label: 'Runner-Up',
  },
  contender: {
    border: 'border-l-gray-300 dark:border-l-gray-600',
    rank: 'text-gray-500 dark:text-gray-400',
    bg: '',
    chip: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    label: 'Contender',
  },
  none: {
    border: 'border-l-transparent',
    rank: 'text-gray-400 dark:text-gray-500',
    bg: '',
    chip: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    label: '—',
  },
};

const fmtScore = (v) =>
  v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(2);

const fmtPct = (v) =>
  v == null || Number.isNaN(Number(v)) ? '—' : `${Math.round(Number(v) * 100)}%`;

const fmtDelta = (diff) => {
  if (diff == null || Number.isNaN(Number(diff))) return '—';
  if (Math.abs(diff) < 0.005) return '±0.00';
  const sign = diff > 0 ? '+' : '−';
  return `${sign}${Math.abs(diff).toFixed(2)}`;
};

const deltaClass = (diff) => {
  if (diff == null || Math.abs(diff) < 0.005) {
    return 'text-gray-400 dark:text-gray-500';
  }
  // Lower error = better. Negative diff vs self means peer is better than you.
  return diff < 0
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-red-500 dark:text-red-400';
};

const h2hClass = (h2h) => {
  if (!h2h || h2h.coDays === 0) return 'text-gray-400 dark:text-gray-500';
  const net = h2h.wins - h2h.losses;
  if (net === 0) return 'text-gray-500 dark:text-gray-400';
  return net > 0
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-red-500 dark:text-red-400';
};

const displayName = (row, blurSensitive) => {
  if (blurSensitive) {
    return row.username || row.email || `User ${row.user_id}`;
  }
  return row.email || row.username || `User ${row.user_id}`;
};

/**
 * Compact peer leaderboard. Each row shows the forecaster's score and the
 * difference vs the logged-in user, with row colors keyed to the league
 * tier (Elite / Challenger / Runner-Up / Contender). Top-5 rows still get
 * the payment-cutoff separator; the self row is always highlighted indigo.
 *
 * Unqualified rows (`unqualified: true`) render with an amber warning,
 * dimmed/italic score and a "Not evaluated" subtext. They are still
 * visible — qualification only gates league rewards, not visibility.
 *
 * Mode is set explicitly by the caller:
 *
 *   - **`mode="rankings"` (head-to-head).** Rows are expected to carry
 *     `daily_scores` and `field_win_rate`. Two right-hand columns: FIELD
 *     W% (canonical, fair, same ordering for every viewer) and VS YOU
 *     (per-pair tally over days both viewer and peer submitted). Tier
 *     coloring and the payment-cutoff line are suppressed because the
 *     rail's rank reflects Field W% sort, not the league/payment rank.
 *   - **`mode="errors"` (default).** Rows carry a single `score` (lower is
 *     better). Two right-hand columns: the metric value and Δ vs you
 *     (signed score gap). Tier coloring + payment cutoff render as before.
 *
 * @param {{
 *   rows: Array<{
 *     user_id: number, rank: number, score: number,
 *     username?: string, email?: string,
 *     unqualified?: boolean,
 *     n_days_w_penalties?: number,
 *     field_win_rate?: number,
 *     daily_scores?: Array<{ target_day: string, score: number }>,
 *   }>,
 *   selfUserId?: number | null,
 *   mode?: 'errors' | 'rankings',
 *   metricLabel?: string,
 *   thresholds?: { elite?: number, challenger?: number, runner_up?: number } | null,
 *   showPaymentCutoff?: boolean,
 *   maxMissedDays?: number,
 *   blurSensitive?: boolean,
 *   onSelectForecaster?: ((userId: number) => void) | null,
 *   className?: string,
 * }} props
 *
 * When `onSelectForecaster` is provided, every non-self row becomes an
 * interactive control that re-pivots the analytics view to that forecaster
 * (admins / market owners only — the parent decides whether to pass it).
 * The self row stays static since it is already the active selection.
 */
export default function PeerLeaderboardRail({
  rows = [],
  selfUserId = null,
  mode = 'errors',
  metricLabel = 'Score',
  thresholds = null,
  showPaymentCutoff = true,
  maxMissedDays = 5,
  blurSensitive = false,
  onSelectForecaster = null,
  className = '',
}) {
  const interactive = typeof onSelectForecaster === 'function';
  const scrollRef = useRef(null);
  const selfRowRef = useRef(null);
  // One-shot highlight on the self row when the selection changes — the
  // in-context confirmation that a row click (or dropdown pick) re-pivoted
  // the view to this forecaster. Replaces a floating toast.
  const [justSelected, setJustSelected] = useState(false);
  const prevSelfRef = useRef(selfUserId);
  // Drive fade overlays: hidden at the edge so the first/last row isn't
  // gratuitously washed out. The fades signal "more content" — the subtle
  // scrollbar alone is too easy to miss.
  const [atScrollStart, setAtScrollStart] = useState(true);
  const [atScrollEnd, setAtScrollEnd] = useState(true);

  const attachSelfRowRef = useCallback((node) => {
    selfRowRef.current = node;
  }, []);

  // Track scroll position so the top/bottom fades hide at the edges.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setAtScrollStart(scrollTop <= 4);
      setAtScrollEnd(scrollHeight - scrollTop - clientHeight <= 4);
    };
    update();
    container.addEventListener('scroll', update, { passive: true });
    return () => container.removeEventListener('scroll', update);
  }, [rows]);

  // Center the self row once, the first time it exists. We deliberately do
  // NOT re-center on every selection change: when an admin clicks a peer to
  // re-pivot, that row is already in view, so auto-scrolling the list would
  // be unwanted motion (reads as the leaderboard "jumping"). Centering only
  // on first appearance still gives the forecaster's own view a centered
  // self row on load.
  const hasCenteredRef = useRef(false);
  useEffect(() => {
    if (hasCenteredRef.current) return;
    const container = scrollRef.current;
    const target = selfRowRef.current;
    if (!container || !target) return;
    const top = target.offsetTop - container.clientHeight / 2 + target.clientHeight / 2;
    container.scrollTop = Math.max(0, top);
    hasCenteredRef.current = true;
  }, [rows, selfUserId]);

  // Pulse the self row once when the selection actually changes (not on first
  // mount, so the rail doesn't flash every time it appears).
  useEffect(() => {
    if (prevSelfRef.current === selfUserId) return;
    const changedFrom = prevSelfRef.current;
    prevSelfRef.current = selfUserId;
    if (changedFrom == null || selfUserId == null) return;
    setJustSelected(true);
    const t = window.setTimeout(() => setJustSelected(false), 1100);
    return () => window.clearTimeout(t);
  }, [selfUserId]);

  if (!rows.length) return null;
  // All rows render — the list is scrollable inside the card, with the
  // self row centered by default. Windowing is preserved as a testable
  // helper but no longer trims the visible list.
  const visible = rows;
  const selfRow = rows.find((r) => r.user_id === selfUserId);
  const selfRank = selfRow?.rank;
  const selfScore = selfRow?.score != null ? Number(selfRow.score) : null;
  const showDelta = selfScore != null;

  const isHeadToHead = mode === 'rankings';
  const viewerDailyScores = isHeadToHead ? (selfRow?.daily_scores || []) : null;

  return (
    <section className={`h-full flex flex-col ${className}`.trim()}>
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
            Peers
          </div>
          <h3 className="mt-1.5 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
            Who&apos;s around you in the field
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Head-to-head record against each forecaster on shared submission days.
          </p>
        </div>
        {selfRank != null ? (
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300 whitespace-nowrap">
            YOU · #{selfRank}
          </span>
        ) : null}
      </header>
      <div className="flex-1 flex flex-col rounded-xl bg-surface dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-sm p-3.5">
        <div className="flex items-center gap-3 px-2 pb-1.5 mb-1 border-b border-gray-100 dark:border-gray-700 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          <div className="w-9">Rank</div>
          <div className="flex-1 min-w-0">Forecaster</div>
          {isHeadToHead ? (
            <>
              <div
                className="w-16 text-right tabular-nums flex items-center justify-end gap-1"
                title={
                  'vs Field — pairwise win rate across all peers.\n' +
                  'Example: 74% means this forecaster has a better score than the peer on 74% of co-submitted days, averaged across the field.'
                }
              >
                vs Field
                <InformationCircleIcon
                  className="h-3 w-3 text-gray-400 dark:text-gray-500 flex-none"
                  aria-hidden="true"
                />
              </div>
              <div
                className="w-24 text-right tabular-nums flex items-center justify-end gap-1"
                title={
                  'vs You — head-to-head only on days you both submitted.\n' +
                  'Example: 7-9 (16d) means on the 16 days you both submitted, you won 7 and they won 9.'
                }
              >
                vs You
                <InformationCircleIcon
                  className="h-3 w-3 text-gray-400 dark:text-gray-500 flex-none"
                  aria-hidden="true"
                />
              </div>
            </>
          ) : (
            <>
              <div
                className="w-20 text-right tabular-nums"
                title={`Average monthly ${String(metricLabel).toLowerCase()} across each forecaster's own submitted days`}
              >
                {String(metricLabel).toUpperCase()}
              </div>
              <div className="w-20 text-right tabular-nums">Δ vs you</div>
            </>
          )}
        </div>
        <div className="relative flex-1 min-h-0">
          <div ref={scrollRef} className="subtle-scrollbar h-full max-h-[340px] overflow-y-auto">
          <ol>
          {visible.map((r, i) => {
            const prev = i > 0 ? visible[i - 1] : null;
            const prevWasCutoff = prev && !prev.__sep && prev.rank === 5;
            const showRowDivider = i > 0 && !prevWasCutoff;
            if (r.__sep) {
              return (
                <li
                  key={`sep-${i}`}
                  className={`py-1 text-center text-[11px] text-gray-400 dark:text-gray-500 ${
                    showRowDivider ? 'border-t border-gray-100 dark:border-gray-700' : ''
                  }`}
                >
                  … {r.count} other{r.count === 1 ? '' : 's'} …
                </li>
              );
            }
            const isSelf = r.user_id === selfUserId;
            const isUnqualified = r.unqualified === true;
            // Rank-based tier matches the league assignment exactly. Score-based
            // tiering is only used as a fallback when rank is missing (e.g. the
            // Rankings rail, where rows aren't pre-tiered).
            // In head-to-head (rankings) mode the rail's rank reflects Field W%
            // sort, not the league/payment rank — so tier coloring would be
            // misleading. Force-neutralize.
            const tierKey = isUnqualified || isHeadToHead
              ? 'none'
              : (tierForRank(r.rank) || tierForScore(r.score, thresholds) || 'none');
            const tier = tierStyles[tierKey];
            const isLast = i === visible.length - 1;
            const showCutoff =
              !isHeadToHead && showPaymentCutoff && r.rank === 5 && !isLast && !isUnqualified;

            const rowBg = isSelf ? 'bg-brand-50 dark:bg-brand-500/10' : tier.bg;
            const borderClass = isSelf
              ? 'border-l-brand-500'
              : isUnqualified
                ? 'border-l-amber-400 dark:border-l-amber-500'
                : tier.border;

            const diff = !isHeadToHead && showDelta && r.score != null && !isSelf && !isUnqualified
              ? Number(r.score) - selfScore
              : null;

            const h2h = isHeadToHead && !isSelf && !isUnqualified
              ? h2hAgainstViewer(r.daily_scores, viewerDailyScores)
              : null;
            const lowOverlap = h2h != null && h2h.coDays > 0 && h2h.coDays < LOW_OVERLAP_THRESHOLD;
            const noOverlap = h2h != null && h2h.coDays === 0;
            const h2hTooltip = h2h && h2h.coDays > 0
              ? `Won ${h2h.wins} · Lost ${h2h.losses}${h2h.ties ? ` · Tied ${h2h.ties}` : ''} over ${h2h.coDays} co-submitted day${h2h.coDays === 1 ? '' : 's'}${lowOverlap ? ' (limited overlap)' : ''}`
              : (noOverlap ? 'No co-submitted days with you' : undefined);

            const penaltyDays = r.n_days_w_penalties;
            const unqualifiedSubtext = penaltyDays != null
              ? `Not evaluated · ${penaltyDays} penalty day${penaltyDays === 1 ? '' : 's'} (max ${maxMissedDays})`
              : `Not evaluated · exceeded ${maxMissedDays}-day allowance`;

            const rowInteractive = interactive && !isSelf;
            const rowName = displayName(r, blurSensitive);

            return (
              <React.Fragment key={r.user_id}>
                <li
                  ref={isSelf ? attachSelfRowRef : undefined}
                  role={rowInteractive ? 'button' : undefined}
                  tabIndex={rowInteractive ? 0 : undefined}
                  // Prevent the row from grabbing focus on click: focusing an
                  // element inside the overflow container makes the browser
                  // scroll it fully into view (another source of "jumping").
                  // The row stays keyboard-focusable via Tab for a11y.
                  onMouseDown={rowInteractive ? (e) => e.preventDefault() : undefined}
                  onClick={rowInteractive ? () => onSelectForecaster(r.user_id) : undefined}
                  onKeyDown={
                    rowInteractive
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelectForecaster(r.user_id);
                          }
                        }
                      : undefined
                  }
                  aria-label={rowInteractive ? `View analytics for ${rowName}` : undefined}
                  className={`group flex items-start gap-3 px-2 py-1.5 rounded-md text-sm border-l-2 transition-shadow duration-300 ${borderClass} ${rowBg} ${isUnqualified ? 'opacity-80' : ''} ${
                    isSelf && justSelected ? 'ring-2 ring-brand-400 dark:ring-brand-400' : ''
                  } ${
                    rowInteractive
                      ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset transition-colors'
                      : ''
                  }`}
                  title={
                    isUnqualified
                      ? unqualifiedSubtext
                      : rowInteractive
                        ? 'View this forecaster'
                        : undefined
                  }
                >
                  <div
                    className={`w-9 font-bold font-mono tabular-nums ${
                      isUnqualified ? 'text-amber-500 dark:text-amber-400' : tier.rank
                    }`}
                  >
                    {isUnqualified ? '—' : `#${r.rank}`}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`truncate ${
                          isSelf
                            ? 'font-semibold text-brand-700 dark:text-brand-300'
                            : 'text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {rowName}
                      </span>
                      {isUnqualified ? (
                        <ExclamationTriangleIcon
                          className="h-3.5 w-3.5 flex-none text-amber-500 dark:text-amber-400"
                          title={unqualifiedSubtext}
                        />
                      ) : null}
                      {isSelf ? (
                        <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300">
                          YOU
                        </span>
                      ) : null}
                      {rowInteractive ? (
                        <ChevronRightIcon
                          className="h-3.5 w-3.5 flex-none text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity"
                          aria-hidden="true"
                        />
                      ) : null}
                    </div>
                    {isUnqualified ? (
                      <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 leading-tight">
                        {unqualifiedSubtext}
                      </div>
                    ) : null}
                  </div>
                  {isHeadToHead ? (
                    <>
                      <div
                        className={`w-16 text-right font-mono tabular-nums font-semibold ${
                          isUnqualified
                            ? 'text-gray-400 dark:text-gray-500 italic'
                            : 'text-gray-800 dark:text-gray-100'
                        }`}
                      >
                        {fmtPct(r.field_win_rate ?? r.score)}
                      </div>
                      <div
                        className={`w-24 text-right font-mono tabular-nums font-semibold ${
                          isSelf || isUnqualified
                            ? 'text-gray-300 dark:text-gray-600'
                            : `${h2hClass(h2h)}${lowOverlap ? ' italic opacity-70' : ''}`
                        }`}
                        title={h2hTooltip}
                      >
                        {isSelf || isUnqualified ? (
                          '—'
                        ) : noOverlap ? (
                          <span className="text-gray-400 dark:text-gray-500">— (0d)</span>
                        ) : (
                          <>
                            <span>{`${h2h.wins}-${h2h.losses}`}</span>
                            {' '}
                            <span className="font-normal text-[10px] text-gray-500 dark:text-gray-400">
                              {`(${h2h.coDays}d)`}
                            </span>
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className={`w-20 text-right font-mono tabular-nums font-semibold ${
                          isUnqualified
                            ? 'text-gray-400 dark:text-gray-500 italic'
                            : 'text-gray-800 dark:text-gray-100'
                        }`}
                      >
                        {fmtScore(r.score)}
                      </div>
                      <div className={`w-20 text-right font-mono tabular-nums font-semibold ${
                        isSelf || isUnqualified ? 'text-gray-300 dark:text-gray-600' : deltaClass(diff)
                      }`}>
                        {isSelf || isUnqualified ? '—' : fmtDelta(diff)}
                      </div>
                    </>
                  )}
                </li>
                {showCutoff ? (
                  <li
                    aria-hidden="true"
                    className="flex items-center gap-2 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold text-emerald-700/80 dark:text-emerald-400/80"
                  >
                    <span className="flex-1 h-px bg-emerald-300/60 dark:bg-emerald-500/40" />
                    <span>Payment cutoff</span>
                    <span className="flex-1 h-px bg-emerald-300/60 dark:bg-emerald-500/40" />
                  </li>
                ) : null}
              </React.Fragment>
            );
          })}
          </ol>
          </div>
          {/* Top + bottom fades — universal "more content" signal so users
              don't miss the subtle scrollbar. Each hides itself at its
              respective edge so the first/last row isn't washed out. */}
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-surface dark:from-gray-870 to-transparent transition-opacity duration-200 ${atScrollStart ? 'opacity-0' : 'opacity-100'}`}
          />
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-surface dark:from-gray-870 to-transparent transition-opacity duration-200 ${atScrollEnd ? 'opacity-0' : 'opacity-100'}`}
          />
        </div>

        {thresholds ? (
          <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wider font-semibold">
            <span className="text-gray-400 dark:text-gray-500">Tiers</span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 ${tierStyles.elite.chip}`}>Elite</span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 ${tierStyles.challenger.chip}`}>Challenger</span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 ${tierStyles.runner_up.chip}`}>Runner-Up</span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 ${tierStyles.contender.chip}`}>Contender</span>
          </div>
        ) : null}

        {isHeadToHead ? (
          // Footnote sits right under the tier legend (or row list) rather
          // than pinned to the bar bottom — when the leaderboard has few
          // rows, `mt-auto` would otherwise leave a tall void between the
          // legend and the footnote, breaking visual parity with the
          // neighbouring LeagueTimelineCard's bottom-of-card note.
          <div className="mt-3 text-[11px] text-gray-500 dark:text-gray-400 leading-snug space-y-1">
            <p>
              <span className="font-semibold text-gray-700 dark:text-gray-300">vs Field</span>
              {' '}— pairwise win rate across all peers
              {' '}
              <span className="text-gray-400 dark:text-gray-500">
                (e.g. 74% = wins 74% of co-submitted days against the field).
              </span>
            </p>
            <p>
              <span className="font-semibold text-gray-700 dark:text-gray-300">vs You</span>
              {' '}— head-to-head on days you both submitted
              {' '}
              <span className="text-gray-400 dark:text-gray-500">
                (e.g. 7-9 (16d) = you won 7, they won 9 across 16 shared days).
              </span>
            </p>
          </div>
        ) : (
          <>
            <p className="mt-3 text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
              Ranked by monthly {String(metricLabel).toLowerCase()}
              {showPaymentCutoff ? ' · ranks 1–5 Elite, 6–10 Challenger, 11 Runner-Up receive league payments.' : '.'}
            </p>
            <p className="pt-1 text-[10px] text-gray-500 dark:text-gray-400 leading-snug">
              Each forecaster is averaged over the days they submitted on, so submission windows can differ across rows.
            </p>
          </>
        )}
      </div>
    </section>
  );
}
